import React from 'react'
import { motion } from 'framer-motion'
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'

// Utility function to safely convert any value to a number
const toNumber = (value: any): number => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? 0 : parsed
  }
  return 0
}

interface IntelligenceMetricsData {
  behavior_effectiveness: {
    natural_score: number
    detection_avoidance: number
    engagement_quality: number
    response_timing_accuracy: number
  }
  spam_prevention: {
    content_blocked: number
    false_positive_rate: number
    rule_accuracy: Array<{
      rule_name: string
      accuracy: number
      triggers: number
    }>
  }
  crisis_management: {
    response_time: number
    resolution_rate: number
    escalation_prevention: number
    sentiment_improvement: number
  }
  learning_insights: {
    patterns_discovered: number
    rules_optimized: number
    behavior_adaptations: number
    confidence_score: number
  }
  trends: {
    weekly_improvement: number
    monthly_performance: number
    quarter_outlook: 'improving' | 'stable' | 'declining'
  }
}

interface IntelligenceMetricsProps {
  data?: IntelligenceMetricsData
}

const IntelligenceMetrics: React.FC<IntelligenceMetricsProps> = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <ChartBarIcon className="w-6 h-6 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Intelligence Metrics</h3>
        </div>
        <p className="text-gray-500 text-center py-8">No metrics data available</p>
      </div>
    )
  }

  const { behavior_effectiveness, spam_prevention, crisis_management, learning_insights, trends } = data

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100'
    if (score >= 75) return 'text-blue-600 bg-blue-100'
    if (score >= 60) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <ArrowTrendingUpIcon className="w-4 h-4 text-green-600" />
    if (trend < 0) return <ArrowTrendingDownIcon className="w-4 h-4 text-red-600" />
    return <div className="w-4 h-4" />
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="w-6 h-6 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">Intelligence Metrics</h3>
        </div>
        <div className="flex items-center space-x-2">
          {getTrendIcon(trends.weekly_improvement)}
          <span className="text-sm text-gray-600">
            {toNumber(trends.weekly_improvement) > 0 ? '+' : ''}{toNumber(trends.weekly_improvement).toFixed(1)}% this week
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
        {/* Behavior Effectiveness */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-4 border border-gray-200 rounded-lg"
        >
          <h4 className="text-sm font-medium text-gray-900 mb-3">Behavior Effectiveness</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Natural Score</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(behavior_effectiveness.natural_score)}`}>
                {toNumber(behavior_effectiveness.natural_score).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Detection Avoidance</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(behavior_effectiveness.detection_avoidance)}`}>
                {toNumber(behavior_effectiveness.detection_avoidance).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Engagement Quality</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(behavior_effectiveness.engagement_quality)}`}>
                {toNumber(behavior_effectiveness.engagement_quality).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Timing Accuracy</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(behavior_effectiveness.response_timing_accuracy)}`}>
                {toNumber(behavior_effectiveness.response_timing_accuracy).toFixed(1)}%
              </span>
            </div>
          </div>
        </motion.div>

        {/* Spam Prevention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 border border-gray-200 rounded-lg"
        >
          <h4 className="text-sm font-medium text-gray-900 mb-3">Spam Prevention</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Content Blocked</span>
              <span className="text-sm font-semibold text-red-600">
                {spam_prevention.content_blocked}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">False Positive Rate</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${spam_prevention.false_positive_rate < 5 ? 'text-green-600 bg-green-100' : 'text-yellow-600 bg-yellow-100'}`}>
                {toNumber(spam_prevention.false_positive_rate).toFixed(1)}%
              </span>
            </div>
            <div className="text-xs text-gray-600 mt-2">Top Rules:</div>
            {spam_prevention.rule_accuracy.slice(0, 2).map((rule, index) => (
              <div key={index} className="flex justify-between items-center text-xs">
                <span className="text-gray-500 truncate">{rule.rule_name}</span>
                <span className="font-medium">{toNumber(rule.accuracy).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Crisis Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-4 border border-gray-200 rounded-lg"
        >
          <h4 className="text-sm font-medium text-gray-900 mb-3">Crisis Management</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Response Time</span>
              <span className="text-sm font-semibold text-blue-600">
                {Math.round(crisis_management.response_time / 60)}m
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Resolution Rate</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(crisis_management.resolution_rate)}`}>
                {toNumber(crisis_management.resolution_rate).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Escalation Prevention</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(crisis_management.escalation_prevention)}`}>
                {toNumber(crisis_management.escalation_prevention).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Sentiment Improvement</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(crisis_management.sentiment_improvement)}`}>
                {toNumber(crisis_management.sentiment_improvement).toFixed(1)}%
              </span>
            </div>
          </div>
        </motion.div>

        {/* Learning Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-4 border border-gray-200 rounded-lg"
        >
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <CpuChipIcon className="w-4 h-4 mr-1" />
            Learning Insights
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Patterns Discovered</span>
              <span className="text-sm font-semibold text-purple-600">
                {learning_insights.patterns_discovered}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Rules Optimized</span>
              <span className="text-sm font-semibold text-indigo-600">
                {learning_insights.rules_optimized}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Behavior Adaptations</span>
              <span className="text-sm font-semibold text-green-600">
                {learning_insights.behavior_adaptations}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">Confidence Score</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(learning_insights.confidence_score)}`}>
                {toNumber(learning_insights.confidence_score).toFixed(1)}%
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Performance Trends */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Trends</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center space-x-1 mb-1">
              {getTrendIcon(trends.weekly_improvement)}
              <p className="text-sm font-medium text-gray-900">Weekly</p>
            </div>
            <p className="text-lg font-semibold text-blue-600">
              {toNumber(trends.weekly_improvement) > 0 ? '+' : ''}{toNumber(trends.weekly_improvement).toFixed(1)}%
            </p>
          </div>
          <div className="p-3 bg-indigo-50 rounded-lg">
            <div className="flex items-center justify-center space-x-1 mb-1">
              {getTrendIcon(trends.monthly_performance)}
              <p className="text-sm font-medium text-gray-900">Monthly</p>
            </div>
            <p className="text-lg font-semibold text-indigo-600">
              {toNumber(trends.monthly_performance) > 0 ? '+' : ''}{toNumber(trends.monthly_performance).toFixed(1)}%
            </p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-sm font-medium text-gray-900 mb-1">Outlook</p>
            <p className={`text-lg font-semibold capitalize ${
              trends.quarter_outlook === 'improving' ? 'text-green-600' :
              trends.quarter_outlook === 'stable' ? 'text-blue-600' : 'text-red-600'
            }`}>
              {trends.quarter_outlook}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default IntelligenceMetrics